'use client';

import React, { useEffect, useRef, useState } from 'react';
import Chips from '@/components/chips/Chips';
import clsx from 'clsx';
import { ChipsWrapperProps } from '@/components/chips/types';
import { IconProps } from '@/components/icon/types';
import { useTranslations } from '@/lib';
import { useParams } from 'next/navigation';

const typeIcons: Record<string, IconProps> = {
  artikel: { name: 'pen-tool', size: 16 },
  news: { name: 'newspaper', size: 16 },
  'flash talk': { name: 'zap', size: 16 },
  'edu talk': { name: 'graduation-cap', size: 16 },
  podcast: { name: 'mic', size: 16 },
  webinar: { name: 'video', size: 16 },
  ratgeber: { name: 'book-open', size: 16 },
};

const ChipsContentHubWrapper: React.FC<ChipsWrapperProps> = ({
  options,
  selectedOptions,
  onSelectionChange,
  variant,
  selectionMode = 'multiple',
  resetPosition,
}) => {
  const [selectedChips, setSelectedChips] = useState<string[]>(selectedOptions);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  const { locale } = useParams();
  const { t } = useTranslations();

  useEffect(() => {
    setSelectedChips(selectedOptions);
  }, [selectedOptions]);

  useEffect(() => {
    if (resetPosition && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [resetPosition]);

  const handleChipClick = (chip: string) => {
    let updatedSelection: string[];

    if (selectionMode === 'multiple') {
      updatedSelection = selectedChips.includes(chip)
        ? selectedChips.filter((selected) => selected !== chip)
        : [...selectedChips, chip];
    } else {
      updatedSelection = selectedChips.includes(chip) ? [] : [chip];
    }

    setSelectedChips(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const scrollToChip = (chipElement: HTMLElement) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const chipLeft = chipElement.offsetLeft;
      const chipWidth = chipElement.offsetWidth;

      const scrollPosition = chipLeft - (containerWidth - chipWidth) / 2;

      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleChipClickMobile = (chip: string, index: number) => {
    handleChipClick(chip);

    if (chipsRef.current) {
      const chipElement = chipsRef.current.children[index] as HTMLElement;
      scrollToChip(chipElement);
    }
  };

  return (
    <>
      {/* Mobile Layout */}
      <div
        className="relative w-full overflow-x-auto scroll-smooth xl:hidden"
        ref={containerRef}
        data-testid="mobile-chips"
        style={{
          scrollbarWidth: 'none', // For Firefox
          msOverflowStyle: 'none', // For IE 10+
        }}
      >
        <div
          ref={chipsRef}
          className={clsx(
            'flex gap-2 whitespace-nowrap',
            variant === 'inline-wrap'
              ? 'sm:justify-initial w-fit justify-center rounded-full border border-neutral-50 bg-neutral-0 p-3 lg:rounded-full lg:p-2'
              : 'gap-4',
          )}
        >
          {options.map((option, index) => (
            <Chips
              key={option}
              label={
                option === 'artikel' && locale === 'en'
                  ? t('smzhub.artikel')
                  : option
              }
              variant={'default'}
              size={'medium'}
              icon={typeIcons[option]}
              selectionMode={selectionMode}
              selected={selectedChips.includes(option)}
              onClick={() => handleChipClickMobile(option, index)}
            />
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div
        className={clsx(
          'hidden xl:flex',
          variant === 'inline-wrap'
            ? 'sm:justify-initial w-fit justify-center gap-2 rounded-full border border-neutral-50 bg-neutral-0 p-3 lg:rounded-full lg:p-2'
            : 'gap-4',
        )}
        data-testid="desktop-chips"
      >
        {options.map((option) => (
          <Chips
            key={option}
            label={
              option === 'artikel' && locale === 'en'
                ? t('smzhub.artikel')
                : option
            }
            variant={'default'}
            size={'medium'}
            icon={typeIcons[option]}
            selectionMode={selectionMode}
            selected={selectedChips.includes(option)}
            onClick={() => handleChipClick(option)}
          />
        ))}
      </div>
    </>
  );
};

ChipsContentHubWrapper.displayName = 'ChipsContentHubWrapper';
export default ChipsContentHubWrapper;
